import { FormattedMessage, useIntl } from 'react-intl';

import { useModals } from 'context';

import { Button } from 'components/Button';
import Modal from 'components/Modal';

import { PublishedProfileProps } from './types';

import * as S from './styles';

const PublishedProfile: React.FC<PublishedProfileProps> = ({
  isOpen,
  props,
  modalKey,
}) => {
  const { closeModal } = useModals();
  const { specialistInfo } = props;
  const intl = useIntl();

  const handleSocialNavigation = () => {
    return window.open(specialistInfo.socialLink, '_blank');
  };

  return (
    <Modal isOpen={isOpen} closeModal={() => closeModal(modalKey)}>
      <S.Container>
        <S.ModalHeaderContainer>
          {specialistInfo.avatarURL ? (
            <S.StyledCareProviderAvatar
              data-testid="specialistModalAvatar"
              src={specialistInfo.avatarURL}
              alt="Specialist avatar"
            />
          ) : (
            <S.StyledCareProviderAvatar data-testid="specialistModalAvatarPlaceholder">
              <S.StyledCareProviderAvatarText>
                {specialistInfo.fullName
                  ?.split(' ')
                  .slice(0, 2)
                  .map(word => word[0])
                  .join(',')
                  .replace(',', '')}
              </S.StyledCareProviderAvatarText>
            </S.StyledCareProviderAvatar>
          )}
          <S.ModalHeaderInfoContainer>
            <S.CareProviderFullName data-testid="cpFullName">
              {specialistInfo.fullName}
            </S.CareProviderFullName>
            <S.CareProviderProfessionalInformation data-testid="cpOccupationAndProfessionalNumber">
              {`${intl.formatMessage({
                id: `PROFESSIONAL.${specialistInfo.occupation}`,
              })} - ${specialistInfo.professionalNumber}`}
            </S.CareProviderProfessionalInformation>
            <>
              {!!specialistInfo.socialLink && (
                <S.StyledSocialContainer
                  data-testid="socialModalMedia"
                  onClick={handleSocialNavigation}
                >
                  <S.StyledInstagram />
                  <S.StyledSocialUsername>
                    {specialistInfo.socialUsername}
                  </S.StyledSocialUsername>
                </S.StyledSocialContainer>
              )}
            </>
          </S.ModalHeaderInfoContainer>
        </S.ModalHeaderContainer>
        {!!specialistInfo.videoId && (
          <S.CareProviderVideo
            data-testid="videoIframe"
            src={`https://player.vimeo.com/video/${specialistInfo.videoId}?&title=0&color=710252&byline=0&portrait=0`}
          />
        )}
        <S.SectionsContainer>
          <S.Section>
            <S.SectionTitle data-testid="aboutTitle">
              <FormattedMessage id="PUBLISHED_PROFILE.ABOUT.TITLE" />
            </S.SectionTitle>
            <S.SectionText data-testid="aboutContent">
              {specialistInfo.about}
            </S.SectionText>
          </S.Section>
          <S.Section>
            <S.SectionTitle data-testid="areasTitle">
              <FormattedMessage id="PUBLISHED_PROFILE.AREAS.TITLE" />
            </S.SectionTitle>
            <S.AreasContainer>
              {specialistInfo.specialities?.map((speciality, idx) => (
                <S.StyledTag
                  data-testid={`specialityModal-${idx}`}
                  key={speciality.titleKey}
                >
                  {intl.formatMessage({
                    id: `PARTNERSINFORMATION.PROFESSIONALINFOSTAB2.DROPDOWNSPECIALTIES${speciality.titleKey}`,
                  })}
                </S.StyledTag>
              ))}
            </S.AreasContainer>
          </S.Section>
          <S.Section>
            <S.SectionTitle data-testid="educationTitle">
              <FormattedMessage id="PUBLISHED_PROFILE.EDUCATION.TITLE" />
            </S.SectionTitle>
            <S.SectionText data-testid="educationContent">
              {specialistInfo.education}
            </S.SectionText>
          </S.Section>
        </S.SectionsContainer>

        <Button
          onClick={() => closeModal(modalKey)}
          data-testid="closeModalButton"
        >
          <FormattedMessage id="PUBLISHED_PROFILE.CLOSE_BUTTON" />
        </Button>
      </S.Container>
    </Modal>
  );
};

export default PublishedProfile;
